/**
 * The two dates the 18 September work turns on, read from the one settings row.
 *
 * Both were added because Winnie asked for an existing control to be reused rather than a new
 * one invented — "reuse the Accounting Lock Date, don't invent a new time window" — and
 * neither existed yet.
 *
 * Cached for the life of the page. Both are edited rarely, in Admin → Settings, and every
 * correction form and every AR write would otherwise re-read the same row.
 */
import { supabase } from '../lib/supabase'

export interface PlatformDates {
  /** Everything on or before this is in a closed accounting period. Null = nothing locked. */
  accountingLockDate: string | null
  /** When the PPSR-discharge settlement rule starts applying. Null = gate not turned on. */
  ppsrDischargeGateGoLive: string | null
}

const EMPTY: PlatformDates = { accountingLockDate: null, ppsrDischargeGateGoLive: null }

let cached: PlatformDates | null = null
let inflight: Promise<PlatformDates> | null = null

export async function loadPlatformDates(force = false): Promise<PlatformDates> {
  if (!force && cached) return cached
  if (!force && inflight) return inflight

  inflight = (async () => {
    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('accounting_lock_date, ppsr_discharge_gate_go_live')
        .limit(1)
      if (error) throw error
      const row = (data || [])[0] as Record<string, unknown> | undefined
      cached = {
        accountingLockDate: (row?.accounting_lock_date as string) || null,
        ppsrDischargeGateGoLive: (row?.ppsr_discharge_gate_go_live as string) || null,
      }
      return cached
    } catch (e) {
      /* Settings being unreadable must not break a payment or a correction. Both dates read
         as "not set", which is the permissive direction for the gate and the lock alike —
         a refusal nobody can explain is worse than a control that is briefly off. */
      console.error('Platform dates could not be read', e)
      return EMPTY
    } finally {
      inflight = null
    }
  })()

  return inflight
}

export function forgetPlatformDates(): void {
  cached = null
}
